import { useState } from 'react';
import { trackError } from '#api/errors.ts';
import { useSuspenseOIDCProvidersQuery } from '#api/oidc-providers.queries.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { IdentityProviderDetails } from '../../detail/identity-provider-details/IdentityProviderDetails.tsx';
import { useDeleteIdentityProviderMutation } from './DeleteAdminIdentityProviderDialog.queries.ts';

type Props = {
  oidcProviderID: string;
  onClose: () => void;
};

export function DeleteAdminIdentityProviderDialog({ oidcProviderID, onClose }: Props) {
  const [globalErrorMsg, setGlobalErrorMsg] = useState<string | null>(null);
  const deleteOIDCProvider = useDeleteIdentityProviderMutation();
  const oidcProvidersQuery = useSuspenseOIDCProvidersQuery();
  const oidcProvider = oidcProvidersQuery.data.data.find((o) => o.id === oidcProviderID);

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setGlobalErrorMsg(null);
    onClose();
  }

  // Wait until we have the role details
  if (!oidcProviderID || !oidcProvider) return null;

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete your admin identity provider?</div>}
      disableAccept={deleteOIDCProvider.isPending || !oidcProvider}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={async () => {
        setGlobalErrorMsg(null);
        try {
          await deleteOIDCProvider.mutateAsync(oidcProviderID);
          closeModal();
        } catch (error: unknown) {
          trackError(error);
          setGlobalErrorMsg('We couldn’t delete your identity provider, please try again.');
        }
      }}
    >
      <p className="p mt-0">
        Deleting your admin identity provider will immediately remove the ability for members of your organization to
        login to this admin panel using SSO.
      </p>
      <p className="p">Are you sure you want to delete your admin identity provider?</p>
      <IdentityProviderDetails idp={oidcProvider} />
      {globalErrorMsg && <ErrorMessage className="mt-32">{globalErrorMsg}</ErrorMessage>}
    </ConfirmDialog>
  );
}
